import { Exception, ToasterService, Validator } from '@anddone/ui-utilities';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AuthType, ModuleName, ModuleNameMapping } from '../../../../../enum/webhook.enum';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { catchError, forkJoin, of, Subject, Subscription, takeUntil } from 'rxjs';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Column } from '../../shared/tablefilters/column.model';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  NgbDropdownConfig,
  NgbDropdownModule,
  NgbModal,
  NgbModalConfig,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { DeveloperService } from '../../../../../services/developer.service';
import { MessageConstants } from '../../../../../constants/message.constant';
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-webhook-management',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    TranslateModule,
    TableComponent,
    NgbTooltipModule,
  ],
  templateUrl: './webhook-management.html',
  styleUrl: './webhook-management.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe, NgbDropdownConfig],
})
export class WebhookManagement implements OnInit, OnDestroy {
  @ViewChild('closeAddModal') closeAddModal!: ElementRef;
  loggedInUserData: any = {};
  webhookList: any = [];
  webhookEventList: any = [];
  selectedModules: any;
  selectModuleFlag = false;
  selectVersionFlag = false;
  hmacKeyBase64: any = '';
  showHmacKeyBase64Field = false;
  versions: any;
  getCurrAuthType: any;
  modalTitle = 'New';
  isFirstLoad = true;

  // // Form variables
  validator: Validator;
  addWebhookForm: any;
  addWebhookFormErrors: any = {};
  premiumFinanceFeature: any;
  disableFinancing!: boolean;
  showVersionTooltip = false;
  isLoader_addWebhook = signal<boolean>(false);

  authenticationTypes = [
    { id: AuthType.NoAuth, name: 'No Authentication', value: 'NoAuth' },
    { id: AuthType.ApiKey, name: 'API Key', value: 'ApiKey' },
    { id: AuthType.BearerToken, name: 'Bearer Token ', value: 'BearerToken' },
    { id: AuthType.BasicAuth, name: 'Basic Authentication', value: 'BasicAuth' },
  ];

  moduleList: any = [];

  config = {
    WebhookURL: {
      required: { name: ValidationConstant.webhookSetting.webhookURL.name },
      pattern: { name: ValidationConstant.webhookSetting.webhookURL.name },
      maxlength: {
        name: ValidationConstant.webhookSetting.webhookURL.name,
        max: ValidationConstant.webhookSetting.webhookURL.maxLength.toString(),
      },
    },
    Version: {},
    Modules: { required: { name: ValidationConstant.webhookSetting.modules.name } },
    AuthenticationType: {
      required: { name: ValidationConstant.webhookSetting.authenticationType.name },
    },
    HmacKey: {
      required: { name: ValidationConstant.webhookSetting.hmacKey.name },
      minlength: {
        name: ValidationConstant.webhookSetting.hmacKey.name,
        min: ValidationConstant.webhookSetting.hmacKey.minLength.toString(),
      },
      maxlength: {
        name: ValidationConstant.webhookSetting.hmacKey.name,
        max: ValidationConstant.webhookSetting.hmacKey.maxLength.toString(),
      },
      pattern: { name: ValidationConstant.webhookSetting.hmacKey.name },
    },
    Key: {
      required: { name: ValidationConstant.webhookSetting.key.name },
      pattern: { name: ValidationConstant.webhookSetting.key.name },
    },
    Value: {
      required: { name: ValidationConstant.webhookSetting.value.name },
      pattern: { name: ValidationConstant.webhookSetting.value.name },
    },
    URL: {
      required: { name: ValidationConstant.webhookSetting.url.name },
      pattern: { name: ValidationConstant.webhookSetting.url.name },
    },
    Username: { required: { name: ValidationConstant.webhookSetting.username.name } },
    Password: { required: { name: ValidationConstant.webhookSetting.password.name } },
    Response: {
      required: { name: ValidationConstant.webhookSetting.response.name },
      pattern: { name: ValidationConstant.webhookSetting.response.name },
    },
    BearerToken: { required: { name: ValidationConstant.webhookSetting.bearerToken.name } },
  };

  /*** Local variables needed for table filter system */
  searchParamsData: any = {};
  pager: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  rowClickActionSub!: Subscription; // Optional subscription for use with alternate row click behavior
  private destroy$ = new Subject<void>();

  /**** Defined table details & columns ****/
  tableDetails: TableDetails = {
    tableId: 'webhook-management-table',
    allowRowClick: false,
    rowClickURI: undefined,
    rowClickCallback: true, // Demonstration of optional alternative row behavior.
    tableTitle: 'Webhooks',
    defaultItemPerPage: ValidationConstant.defaultPageSize,
    sendDefault90DayFilter: false,
  };
  columns: Column[] = [
    {
      name: 'Status',
      sortState: 'none',
      isSortable: true,
      display: 'status',
      responseKey: 'subscriptionStatus',
      requestKey: 'SubscriptionStatus',
      statusColors: {
        Active: 'green',
        Paused: 'red',
      },
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Active', key: 'Active', checked: false },
          { label: 'Inactive', key: 'Inactive', checked: false },
        ],
      },
    },
    {
      name: 'URL',
      sortState: 'none',
      isSortable: true,
      display: 'normal',
      responseKey: 'webhookUrl',
      requestKey: 'webhookUrl',
      filter: {
        type: 'string',
      },
    },
    {
      name: 'Private Key',
      sortState: 'none',
      isSortable: true,
      display: 'concealed',
      displayOptions: ['addCopyButton'],
      responseKey: 'privateKey',
      requestKey: 'PrivateKey',
      filter: {
        type: 'string',
      },
    },

    {
      name: 'Modules',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'moduleName',
      requestKey: 'moduleName',
      filter: {
        type: 'multipleChoice',
        options: [
          { label: 'Report', key: 'Report', checked: false },
          { label: 'Transaction', key: 'Transaction', checked: false },
          { label: 'Payment Link', key: 'PaymentLink', checked: false },
          { label: 'Account Token', key: 'AccountToken', checked: false },
          { label: 'Token Link', key: 'TokenLink', checked: false },
          { label: 'CBNacha', key: 'CBNacha', checked: false },
          { label: 'Premium Finance', key: 'PremiumFinance', checked: false },
          { label: 'Refund', key: 'TransactionRefund', checked: false },
          { label: 'Chargeback', key: 'TransactionChargeback', checked: false },
          { label: 'Void', key: 'TransactionVoid', checked: false },
          { label: 'Merchant Management', key: 'MerchantManagement', checked: false },
          { label: 'Account Payout', key: 'AccountPayout', checked: false },
          { label: 'Payout Capability', key: 'PayoutCapability', checked: false },
        ],
      },
    },
    {
      name: 'Event Types',
      sortState: 'none',
      isSortable: false,
      display: 'normal',
      responseKey: 'eventTypes',
      requestKey: 'eventTypes',
      filter: undefined,
    },
    {
      name: 'Actions',
      sortState: 'none',
      isSortable: false,
      display: 'actions',
      responseKey: 'operations',
      requestKey: null,
      filter: undefined,
    },
  ];
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private toasterService = inject(ToasterService);
  private allyCommonService = inject(AllyCommonService);
  private developerService = inject(DeveloperService);
  private tableFilterService = inject(TableFilterService);

  constructor(config: NgbDropdownConfig, modalConfig: NgbModalConfig) {
    this.validator = new Validator(this.config);
    config.autoClose = 'outside';
    modalConfig.scrollable = true;
  }

  ngOnInit(): void {
    this.getModuleList();
    this.loggedInUserData = this.allyCommonService.getLoggedInData();

    this.addWebhookForm = this.formBuilder.group({
      WebhookURL: [
        null,
        [
          Validators.required,
          Validators.pattern(ValidationConstant.url_regex),
          Validators.maxLength(ValidationConstant.webhookSetting.webhookURL.maxLength),
        ],
      ],
      Modules: [null, [Validators.required]],
      Version: [null],
      AuthenticationType: [null],
      HmacKey: [
        null,
        [
          Validators.pattern(ValidationConstant.spaceNotAccepted_regex),
          Validators.maxLength(ValidationConstant.webhookSetting.hmacKey.maxLength),
          Validators.minLength(ValidationConstant.webhookSetting.hmacKey.minLength),
        ],
      ],
      Key: [null, [Validators.pattern(ValidationConstant.spaceNotAccepted_regex)]],
      Value: [null, [Validators.pattern(ValidationConstant.spaceNotAccepted_regex)]],
      URL: [null, [Validators.pattern(ValidationConstant.url_regex)]],
      Username: [null, []],
      Password: [null, []],
      BearerToken: [null],
      Response: [null, []],
    });

    this.getCurrAuthType = this.authenticationTypes[0];

    this.addWebhookForm.valueChanges.subscribe((data: any) => this.onValueChanged(data));

    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.tableActionSub = this.tableFilterService.tableActionEvent.subscribe((operationObject) => {
      this.onUserOperationClick(operationObject.operation, operationObject.row);
    });

    // Optional subscription, only used if  tableDetails.allowRowClick and tableDetails.rowClickCallback are both true.
    // Additionally tableDetails.rowClickURI must be false.
    this.rowClickActionSub = this.tableFilterService.rowClickCallbackEvent.subscribe(() => {
      // This is an example of a callback on table click. Add your method or other logic below.
      // alert( 'A row was clicked! This is a demonstration of the alternate row click behavior.' );
    });

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;
        /*** Assign the method that manages your API call here. ***/
        this.getWebhookList(this.pager.currentPage);
      });

    this.tableFilterService.registerSubscriptions(
      this.searchInputSub,
      this.tableActionSub,
      this.rowClickActionSub
    );
    /*** End of Table filter subscription handling ***/
  }

  getModuleList(): void {
    this.developerService.getModuleListForAlly()?.subscribe({
      next: (res: any) => {
        this.moduleList = Object.keys(res)
          .map((key) => {
            const moduleName = res[key];
            return {
              id: Number(key),
              name: ModuleNameMapping[moduleName as keyof typeof ModuleNameMapping] || moduleName,
              moduleName: moduleName,
              value: false,
            };
          })
          .sort((a, b) => a.id - b.id); // Sort by ID
      },
      error: (e: any) => {
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  /*** Defined operations to execute from table events */
  onUserOperationClick(operation: any, row: any) {
    if (operation.key == 'delete') {
      this.deleteWebhook(row);
    }
    if (operation.key == 'view') {
      this.developerService.setSelectedWebhook(row);
      this.router.navigate(['/ally/webhook-events']);
    }
  }

  // form on change value
  onValueChanged(_data?: any) {
    if (!this.addWebhookForm) {
      return;
    }
    if (this.selectModuleFlag && this.addWebhookForm.controls.Modules.value === false) {
      this.addWebhookForm.controls.Modules.setValue(null);
    }
    this.addWebhookFormErrors = this.validator.validate(this.addWebhookForm);
  }

  getWebhookList(pageNumber: number) {
    this.allyCommonService.setIsLoader(true);
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    if (this.isFirstLoad) {
      this.searchParamsData.SortField = 'CreatedOn';
      this.isFirstLoad = false;
    }
    this.developerService
      .getAllWebhook(this.searchParamsData)
      ?.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.allyCommonService.setIsLoader(false);
          this.tableFilterService.setPager(res, pageNumber, this.pager);
          if (res.data.length > 0) {
            res.data.forEach((ele: any) => {
              ele.module = ModuleName[ele.moduleName];
              ele.authTypeName = AuthType[ele.authType];
              ele.eventTypes = [];
              ele.operations = [];
              ele.operations.push({ key: 'delete', value: 'Delete' });
              ele.operations.push({ key: 'view', value: 'View Logs' });
            });
            this.webhookList = res.data;
            this.tableDetails.tableData = this.webhookList || [];
            this.getWebhookEvents(pageNumber);
            /*** Set the table details to include data to be displayed and pagination data and send to tablefilter service */
            this.tableFilterService.setTableDetails(this.tableDetails);
          } else {
            this.webhookList = [];
            this.getWebhookEvents(pageNumber);
            this.tableDetails.tableData = [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e: any) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  /* get all webhook event */
  getWebhookEvents(_pageNumber: number) {
    this.allyCommonService.setIsLoader(true);
    this.developerService
      .getAllWebhookEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.webhookEventList = res.data;
          for (let i = 0; i < this.webhookList?.length; i++) {
            this.webhookList[i].eventTypes = [];
            for (let j = 0; j < this.webhookEventList?.length; j++) {
              if (this.webhookList[i].moduleName == this.webhookEventList[j].moduleName) {
                const moduleName = this.webhookEventList[j].moduleName;
                const eventing = this.webhookEventList[j].description;
                const eventType = moduleName + ' - ' + eventing;
                this.webhookList[i].eventTypes.push(eventType);
              }
            }
            this.webhookList[i].eventTypes = this.webhookList[i].eventTypes?.join(', ');
            if (!this.webhookList[i].privateKey) {
              this.webhookList[i].privateKey = '--';
            }
          }
          this.allyCommonService.setIsLoader(false);
        },
        error: (e: any) => {
          this.allyCommonService.setIsLoader(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  renderVersionDropdown() {
    this.showVersionTooltip = false;
    if (this.selectedModules?.length === 1) {
      this.getModuleVersion(this.selectedModules[0]);
      this.addWebhookForm.controls.Version.setValidators([Validators.required]);
      this.addWebhookForm.controls.Version.updateValueAndValidity();
      this.selectVersionFlag = true;
      this.showVersionTooltip = false;
    } else if (this.selectedModules?.length > 1) {
      this.versions = '1'.split(',');
      this.addWebhookForm.controls.Version?.patchValue(this.versions[0]);
      this.addWebhookForm.controls.Version?.setValidators(null);
      this.addWebhookForm.controls.Version?.updateValueAndValidity();
      this.selectVersionFlag = false;
      this.showVersionTooltip = true;
    }
    if (this.selectedModules && this.selectedModules.length == 1) {
      this.addWebhookForm.get('Version')?.enable();
    } else {
      this.addWebhookForm.get('Version')?.disable();
    }
  }

  getSelectedModuleNames(): string {
    return this.selectedModules.join(', ');
  }

  /* bind value on modify */
  bindUpdateValues(data: any) {
    const authValue = JSON.parse(data.authValue);
    const obj = {
      WebhookURL: data.webhookUrl,
      Modules: [null],
      AuthenticationType: [null],
      HmacKey: data.privateKey,
      Key: null,
      Value: null,
      URL: null,
      Username: null,
      Password: null,
      Response: null,
      BearerToken: null,
    };
    if (authValue.length > 0) {
      authValue.forEach((ele: any) => {
        if (ele.Key == 'AuthApiHeader') {
          obj.Key = ele.Value;
        } else if (ele.Key == 'AuthApiValue') {
          obj.Value = ele.Value;
        } else if (ele.Key == 'AuthBasicUsername') {
          obj.Username = ele.Value;
        } else if (ele.Key == 'AuthBasicPassword') {
          obj.Password = ele.Value;
        } else if (ele.Key == 'AuthBearerURL') {
          obj.URL = ele.Value;
        } else if (ele.Key == 'AuthBearerUsername') {
          obj.Username = ele.Value;
        } else if (ele.Key == 'AuthBearerPassword') {
          obj.Password = ele.Value;
        } else if (ele.Key == 'AuthBearerToken') {
          obj.BearerToken = ele.Value;
        }
      });
    }
    const authType = this.authenticationTypes.filter((ele) => {
      return data.authType == ele.id;
    });
    this.getCurrAuthType = authType[0];
    this.moduleList.forEach((ele: any) => {
      ele.value = ele.moduleName == data.moduleName ? true : false;
    });
    this.addWebhookForm.patchValue(obj);
  }

  /* get selected module */
  getSelectedModule(item: any) {
    item.value = !item.value;
    this.selectModuleFlag = this.getModules().length <= 0 ? true : false;
    this.selectedModules = this.getModules();
    this.renderVersionDropdown();
  }

  /* create authValue api reqObj */
  getAuthValue(value: string) {
    let obj: any;
    if (value == 'ApiKey') {
      const API_KEY = [
        { Key: 'AuthApiHeader', Value: this.addWebhookForm.value.Key },
        { Key: 'AuthApiValue', Value: this.addWebhookForm.value.Value },
      ];
      obj = API_KEY;
    } else if (value == 'BearerToken') {
      const Bearer_Token = [
        { Key: 'AuthBearerURL', Value: this.addWebhookForm.value.URL },
        { Key: 'AuthBearerUsername', Value: this.addWebhookForm.value.Username },
        { Key: 'AuthBearerPassword', Value: this.addWebhookForm.value.Password },
        { Key: 'AuthBearerToken', Value: this.addWebhookForm.value.Response },
      ];
      obj = Bearer_Token;
    } else if (value == 'BasicAuth') {
      const Basic_Auth = [
        { Key: 'AuthBasicUsername', Value: this.addWebhookForm.value.Username },
        { Key: 'AuthBasicPassword', Value: this.addWebhookForm.value.Password },
      ];
      obj = Basic_Auth;
    } else {
      obj = [];
    }
    return JSON.stringify(obj);
  }

  /* get selected module id */
  getModules() {
    const module = this.moduleList.filter((ele: any) => {
      return ele.value == true;
    });
    const arr: any = [];
    module.forEach((ele: any) => {
      arr.push(ele.moduleName);
    });
    return arr;
  }

  getModuleVersion(selectedModule: any) {
    const eventVersions = this.webhookEventList.find(
      (event: any) => event.moduleName === selectedModule
    );
    this.versions = eventVersions?.supportedVersions?.split(',');
  }

  onVersionSelect(selectedVersion: any) {
    this.selectVersionFlag = false;
    this.addWebhookForm.controls.Version.setValue(selectedVersion);
    this.addWebhookForm.controls.Version.markAsDirty();
  }

  openWebhookModalPopup(type: string, content: any) {
    this.modalTitle = type;
    this.showHmacKeyBase64Field = false;
    this.hmacKeyBase64 = '';
    this.addWebhookForm.reset();
    this.selectedModules = [];
    this.renderVersionDropdown();
    this.moduleList.forEach((ele: any) => {
      ele.value = false;
    });
    this.getCurrAuthType = this.authenticationTypes[0];
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
    this.selectModuleFlag = false;
  }

  /* create add webhook reqObj */
  createRequestObj() {
    let obj;
    const objArray = [];
    for (let i = 0; i < this.getModules().length; i++) {
      obj = {
        MerchantId: this.loggedInUserData?.id,
        ModuleName: this.getModules()[i],
        WebhookIds: this.getWebhookEventList(this.getModules()[i]),
        WebhookUrl: this.addWebhookForm.value.WebhookURL,
        ContentType: 'applicationJson',
        HttpMethod: 'HttpPost',
        AuthType: this.getCurrAuthType.value,
        SubscriptionStatus: 'Active',
        AuthValue: this.getAuthValue(this.getCurrAuthType.value),
        HmacRequired: this.addWebhookForm.value.HmacKey ? true : false,
        AutoGenereationKey: false,
        ChangePrivateKey: true,
        PrivateKey: ValidationConstant.base64regex.test(this.addWebhookForm.value.HmacKey)
          ? this.addWebhookForm.value.HmacKey
          : btoa(this.addWebhookForm.value.HmacKey),
      };
      if (this.selectedModules.length === 1) {
        obj = {
          ...obj,
          Version: Number(this.addWebhookForm.controls.Version.value),
        };
      }
      objArray.push(obj);
    }
    return objArray;
  }

  /* get selected webhook events */
  getWebhookEventList(moduleId: any) {
    const event = this.webhookEventList.filter((ele: any) => {
      return ele.moduleName == moduleId;
    });
    const arr: any = [];
    event.forEach((ele: any) => {
      arr.push(ele.id);
    });
    return arr.toString();
  }

  /* validation as per selected auth type */
  getSelectedAuthType(index: any) {
    this.getCurrAuthType = this.authenticationTypes[index];
    if (this.getCurrAuthType.value == 'ApiKey') {
      this.addWebhookForm
        .get('Key')
        .setValidators([
          Validators.required,
          Validators.pattern(ValidationConstant.spaceNotAccepted_regex),
        ]);
      this.addWebhookForm
        .get('Value')
        .setValidators([
          Validators.required,
          Validators.pattern(ValidationConstant.spaceNotAccepted_regex),
        ]);
      this.addWebhookForm.get('Key').updateValueAndValidity();
      this.addWebhookForm.get('Value').updateValueAndValidity();
      this.addWebhookForm.get('Username').setValidators(null);
      this.addWebhookForm.get('Password').setValidators(null);
      this.addWebhookForm.get('Response').setValidators(null);
      this.addWebhookForm.get('URL').setValidators(null);
      this.addWebhookForm.get('Username').updateValueAndValidity();
      this.addWebhookForm.get('Password').updateValueAndValidity();
      this.addWebhookForm.get('Response').updateValueAndValidity();
      this.addWebhookForm.get('URL').updateValueAndValidity();
    } else if (this.getCurrAuthType.value == 'BearerToken') {
      this.addWebhookForm
        .get('URL')
        .setValidators([Validators.required, Validators.pattern(ValidationConstant.url_regex)]);
      this.addWebhookForm.get('Username').setValidators([Validators.required]);
      this.addWebhookForm.get('Password').setValidators([Validators.required]);
      this.addWebhookForm
        .get('Response')
        .setValidators([
          Validators.required,
          Validators.pattern(ValidationConstant.spaceNotAccepted_regex),
        ]);
      this.addWebhookForm.get('Key').setValidators(null);
      this.addWebhookForm.get('Value').setValidators(null);
      this.addWebhookForm.get('Key').updateValueAndValidity();
      this.addWebhookForm.get('Value').updateValueAndValidity();
      this.addWebhookForm.get('URL').updateValueAndValidity();
      this.addWebhookForm.get('Username').updateValueAndValidity();
      this.addWebhookForm.get('Password').updateValueAndValidity();
      this.addWebhookForm.get('Response').updateValueAndValidity();
    } else if (this.getCurrAuthType.value == 'BasicAuth') {
      this.addWebhookForm.get('Username').setValidators([Validators.required]);
      this.addWebhookForm.get('Password').setValidators([Validators.required]);
      this.addWebhookForm.get('Username').updateValueAndValidity();
      this.addWebhookForm.get('Password').updateValueAndValidity();
      this.addWebhookForm.get('Response').setValidators(null);
      this.addWebhookForm.get('Key').setValidators(null);
      this.addWebhookForm.get('Value').setValidators(null);
      this.addWebhookForm.get('URL').setValidators(null);
      this.addWebhookForm.get('Response').updateValueAndValidity();
      this.addWebhookForm.get('Key').updateValueAndValidity();
      this.addWebhookForm.get('Value').updateValueAndValidity();
      this.addWebhookForm.get('URL').updateValueAndValidity();
    } else {
      this.addWebhookForm.get('Key').clearValidators();
      this.addWebhookForm.get('Value').clearValidators();
      this.addWebhookForm.get('URL').clearValidators();
      this.addWebhookForm.get('Username').clearValidators();
      this.addWebhookForm.get('Password').clearValidators();
      this.addWebhookForm.get('Response').clearValidators();
      this.addWebhookForm.updateValueAndValidity();

      this.addWebhookForm.get('Key').setValidators(null);
      this.addWebhookForm.get('Key').setErrors(null);
      this.addWebhookForm.get('Value').setValidators(null);
      this.addWebhookForm.get('Value').setErrors(null);
      this.addWebhookForm.get('URL').setValidators(null);
      this.addWebhookForm.get('URL').setErrors(null);
      this.addWebhookForm.get('Username').setValidators(null);
      this.addWebhookForm.get('Username').setErrors(null);
      this.addWebhookForm.get('Password').setValidators(null);
      this.addWebhookForm.get('Password').setErrors(null);
      this.addWebhookForm.get('Response').setValidators(null);
      this.addWebhookForm.get('Response').setErrors(null);
    }
  }

  /* add webhook call for multiple module */
  addWebhook() {
    const reqObj = this.createRequestObj();
    this.validator.validateAllFormFields(this.addWebhookForm);
    this.addWebhookFormErrors = this.validator.validate(this.addWebhookForm);
    this.selectModuleFlag = this.getModules().length == 0 ? true : false;
    this.selectVersionFlag =
      this.addWebhookForm.controls.Version.value === null && this.selectedModules?.length === 1;

    if (this.addWebhookForm.invalid || this.selectVersionFlag || this.selectModuleFlag) {
      return;
    }
    this.isLoader_addWebhook.set(true);
    const apiCalls = reqObj.map((ele) => this.addWebhookApiCall(ele));

    forkJoin(apiCalls).subscribe(
      (results) => {
        this.isLoader_addWebhook.set(false);
        let errorDisplayed = false;
        let successDisplayed = false;

        results.forEach((result) => {
          if (result instanceof Error) {
            if (!errorDisplayed) {
              const toastMessage = Exception.exceptionMessage(result);
              this.renderVersionDropdown();
              this.selectVersionFlag = false;
              this.toasterService.showError(toastMessage.join(', '), 'full');
              errorDisplayed = true;
            }
          } else {
            const status = result.status || 200;
            if (status === 200 || status === 201) {
              if (!successDisplayed) {
                const msg =
                  this.modalTitle == 'Update'
                    ? MessageConstants.webhook.updateWebhook
                    : MessageConstants.webhook.addWebhook;
                this.toasterService.showSuccess(msg, 'full');
                successDisplayed = true;
                this.closeAddModal.nativeElement.click();
                this.getWebhookList(this.pager.currentPage);
              }
            } else if (!errorDisplayed) {
              const toastMessage = Exception.exceptionMessage(result).join(', ');
              this.renderVersionDropdown();
              this.selectVersionFlag = false;
              this.toasterService.showError(toastMessage, 'full');
              errorDisplayed = true;
            }
          }
        });
      },
      (error) => {
        this.isLoader_addWebhook.set(false);
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      }
    );
  }

  /* add webhook api call */
  addWebhookApiCall(data: any) {
    // Return the observable for the API call
    return this.developerService
      .addWebhook({
        id: null,
        data: data,
      })
      .pipe(
        // Transform the result to include success and error information
        catchError((error) => of(error))
      );
  }

  /* delete webhook api call */
  deleteWebhook(webhook: any) {
    this.allyCommonService.setIsLoader(true);
    this.developerService.deleteWebhook(webhook.id)?.subscribe({
      next: (_res: any) => {
        this.allyCommonService.setIsLoader(false);
        this.toasterService.showSuccess(MessageConstants.webhook.deleteWebhook, 'full');
        this.getWebhookList(1);
      },
      error: (e: any) => {
        this.allyCommonService.setIsLoader(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  onInputChangeEvent(event: any) {
    event.preventDefault();
    this.hmacKeyBase64 = ValidationConstant.base64regex.test(event.target.value)
      ? event.target.value
      : btoa(event.target.value);
    if (event.target.value.length > 29 && event.target.value.length < 251) {
      this.showHmacKeyBase64Field = true;
    } else {
      this.showHmacKeyBase64Field = false;
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Triggers takeUntil
    this.destroy$.complete(); // Final cleanup
  }
}
